/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { findMachineSummary } from 'apis/ut/UtMatrixRegist';
import { UtMatrixDetail, UtMatrixRegistSearchCondition } from 'models/ut/UtMatrix';
import { hasRole } from 'utils/SessionUtil';
import { useLoading } from 'components/process/Loading';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';

/**
 * @desc 작성 > [기계요약] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.01   jshwang12       [고도화] CDA, N2 단위 변경 (Nl/min)
 * 2025.10.01   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리) (ex. 230.456 입력 시 실제 데이터는 230.5 저장)
 * 2026.08.05   jihwan89        CSR E20260727-000228 Vacuum Pump 추가
 */
const MachineSummaryContent = (props: any, ref) => {
  const {
    onSubmit,
    condition = {} as UtMatrixRegistSearchCondition,
    processData = [] as UtMatrixDetail[],
  } = props;
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { isLoading } = useLoading();
  const [rowData, setRowData] = useState<UtMatrixDetail[]>([]);
  const defaultGridHeight = 388;

  const isUtWriter = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return (p.length > 0 && p[0].utmWriteYn === 'Y') || hasRole('ADM');
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);
  const isWrite = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return p.length > 0 && p[0].utmWrtProcProgStatCd === 'UTP02';
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);
  const isWriteComplete = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return p.length > 0 && p[0].utmWrtProcProgStatCd === 'UTP03';
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);

  useImperativeHandle(ref, () => ({
    searchMachineSummary: (condition) => {
      searchMachineSummary(condition);
    },
  }));

  const searchMachineSummary = (condition) => {
    findMachineSummary(condition).then((result) => {
      setRowData(result);
    });
  };

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      isReadOnly: true,
      align: 'center',
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'prdnProcCdNm',
      header: String(t('ut.label.공정', '공정')),
      width: 100,
      align: 'center',
    },
    {
      binding: 'eqclNm',
      header: String(t('ut.label.설비군', '설비군')),
      width: 100,
    },
    {
      binding: 'qty',
      header: String(t('ut.label.라인/설비 수량', '라인/설비 수량')),
      width: 120,
      align: 'center',
      cellTemplate: (params) =>
        `${(params.item.eqpPrdnLnQty || 0).toLocaleString()} / ${(
          params.item.machEqpQty || 0
        ).toLocaleString()}`,
    },
    {
      header: String(t('ut.label.CDA', 'CDA')),
      columns: [
        {
          binding: 'cdaCspSumCapa',
          header: '[Nl/min]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.N2', 'N2')),
      columns: [
        {
          binding: 'nitrPeakCspSumCapa',
          header: 'Peak [Nl/min]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'nitrCspSumCapa', // 기계 조건부 필수 대상값
          header: 'AVG [Nl/min]',
          width: 95,
          isReadOnly: true,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.IW', 'IW')),
      columns: [
        {
          binding: 'iwCspSumCapa',
          header: '[ℓ/min]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.IW2', 'FW')),
      columns: [
        {
          binding: 'frwtCspSumCapa',
          header: '[ℓ/min]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.PCW', 'PCW')),
      columns: [
        {
          binding: 'coolCspSumCapa',
          header: '[ℓ/min]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.WW', 'WW')),
      columns: [
        {
          binding: 'wwCspSumCapa',
          header: '[ℓ/min]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Steam', 'Steam')),
      columns: [
        {
          binding: 'stemCspSumCapa',
          header: '[t/hr]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.NG', 'NG')),
      columns: [
        {
          binding: 'lngCspSumCapa',
          header: '[N㎥/hr]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Return air', 'Return air')),
      columns: [
        {
          binding: 'insdExhaCspSumCapa',
          header: '[CMH]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Exhaust air', 'Exhaust air')),
      columns: [
        {
          binding: 'otsdExhaCspSumCapa',
          header: '[CMH]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
    {
      header: String(t('ut.label.Vacuum Pump', 'Vacuum Pump')),
      columns: [
        {
          binding: 'vacmPumpCspSumCapa',
          header: '[CMH]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Supply Air', 'Supply air')),
      columns: [
        {
          binding: 'suarCspSumCapa',
          header: '[CMH]',
          minWidth: 100,
          width: '*',
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
  ];

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const handleSubmit = () => {
    props?.onSubmit?.();
  };

  const handleModify = () => {
    props?.onModify?.();
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.기계요약', '기계요약')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {condition?.utmNo && condition?.prdnProcCdNm && condition?.eqclNm && (
            <div className="info warning">
              {condition?.utmNo} / {condition?.prdnProcCdNm} / {condition?.eqclNm}
            </div>
          )}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => searchMachineSummary(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={props?.isOpenCondition ? defaultGridHeight + 55 : defaultGridHeight + 120 + 38 + 55}
        frozenColumns={3}
        isSelector={false}
        isFilter={false}
        isReadOnly={true}
        allowPinning={false}
        initialized={onInitialized}
      />
      <GlobalBtnGroup>
        {isWrite && isUtWriter && (
          <Button
            css={IconButton.button}
            className="save"
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {t('com.button.제출', '제출')}
          </Button>
        )}
        {isWriteComplete && isUtWriter && (
          <Button
            css={IconButton.button}
            className="save"
            disabled={isLoading}
            onClick={handleModify}
          >
            {t('com.button.수정', '수정')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default React.forwardRef(MachineSummaryContent);
